import {
  COMMAND_UUID,
  APPLICATION_AUTH_FORMAT_VERSION,
  EVENT_UUID,
  FrameAssembler,
  FrameFlag,
  LIVE_STREAM_UUID,
  PROTOCOL_INFO_UUID,
  PROTOCOL_VERSION,
  REMOTE_SERVICE_UUID,
  fragmentMessage,
  type ProtocolMessage,
} from "./protocol";

export type MessageListener = (message: ProtocolMessage) => void;
export type LiveListener = (record: Uint8Array) => void;
export type DisconnectListener = (reason?: string) => void;
export type ReconnectListener = () => void;

export interface DisconnectOptions {
  reconnect?: boolean;
}

export interface BleTransport {
  readonly applicationAuthRequired?: boolean;
  readonly connected: boolean;
  readonly canReconnect: boolean;
  readonly deviceId?: string;
  pair(): Promise<void>;
  reconnect(preferredDeviceId?: string): Promise<void>;
  disconnect(reason?: string, options?: DisconnectOptions): void;
  markConnectionReady?(): void;
  send(message: ProtocolMessage): Promise<void>;
  onMessage(listener: MessageListener): () => void;
  onLive(listener: LiveListener): () => void;
  onDisconnect(listener: DisconnectListener): () => void;
  onReconnect(listener: ReconnectListener): () => void;
}

export function webBluetoothSupport(): { supported: boolean; reason?: string } {
  if (!window.isSecureContext) return { supported: false, reason: "Bluetooth control needs an HTTPS page." };
  if (!navigator.bluetooth) return { supported: false, reason: "This browser does not provide Web Bluetooth." };
  return { supported: true };
}

export function isCompatibleProtocolInfo(info: Uint8Array): boolean {
  const expected = Uint8Array.of(
    0xe9, PROTOCOL_VERSION, 9, 20, 0, 4, 11, 1,
    APPLICATION_AUTH_FORMAT_VERSION,
  );
  return info.length === expected.length && expected.every((value, index) => info[index] === value);
}

function viewBytes(view: DataView): Uint8Array {
  return new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice();
}

class SupersededConnectionError extends Error {
  constructor() {
    super("Connection attempt superseded.");
    this.name = "SupersededConnectionError";
  }
}

class BleOperationTimeoutError extends Error {
  constructor(operation: string) {
    super(`${operation} timed out.`);
    this.name = "BleOperationTimeoutError";
  }
}

export const BLE_OPERATION_TIMEOUT_MS = Object.freeze({
  connect: 10_000,
  discovery: 5_000,
  read: 3_000,
  subscription: 4_000,
  write: 3_000,
});

async function withDeadline<T>(
  operation: Promise<T>,
  timeoutMs: number,
  label: string,
  onLateResolution?: (value: T) => void,
): Promise<T> {
  let timer: number | undefined;
  let timedOut = false;
  const observed = operation.then((value) => {
    if (timedOut) {
      try {
        onLateResolution?.(value);
      } catch (error) {
        console.warn(`Late ${label.toLowerCase()} cleanup failed`, error);
      }
    }
    return value;
  });
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = window.setTimeout(() => {
      timedOut = true;
      reject(new BleOperationTimeoutError(label));
    }, timeoutMs);
  });
  try {
    return await Promise.race([observed, timeout]);
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

export class WebBleTransport implements BleTransport {
  readonly applicationAuthRequired = true;
  static readonly #AUTO_RECONNECT_INITIAL_DELAY_MS = 800;
  static readonly #AUTO_RECONNECT_MAX_DELAY_MS = 8_000;

  #device?: BluetoothDevice;
  #listeningDevice?: BluetoothDevice;
  #server?: BluetoothRemoteGATTServer;
  #command?: BluetoothRemoteGATTCharacteristic;
  #event?: BluetoothRemoteGATTCharacteristic;
  #live?: BluetoothRemoteGATTCharacteristic;
  #messageListeners = new Set<MessageListener>();
  #disconnectListeners = new Set<DisconnectListener>();
  #reconnectListeners = new Set<ReconnectListener>();
  #eventAssembler = new FrameAssembler();
  #liveListeners = new Set<LiveListener>();
  #writeQueue: Promise<void> = Promise.resolve();
  #connectQueue: Promise<void> = Promise.resolve();
  #intentionalDisconnect = false;
  #retryAfterIntentionalDisconnect = false;
  #disconnectReason?: string;
  #autoReconnectEnabled = false;
  #autoReconnectAttempt = 0;
  #autoReconnectTimer?: number;
  #connectAttemptGeneration = 0;
  #connectionGeneration = 0;

  get connected(): boolean {
    return Boolean(this.#server?.connected && this.#command);
  }

  get canReconnect(): boolean {
    return Boolean(this.#device?.gatt || navigator.bluetooth?.getDevices);
  }

  get deviceId(): string | undefined {
    return this.#device?.id;
  }

  async pair(): Promise<void> {
    const support = webBluetoothSupport();
    if (!support.supported) throw new Error(support.reason);
    const attempt = this.#beginManualConnect();
    const device = await navigator.bluetooth!.requestDevice({
      filters: [{ services: [REMOTE_SERVICE_UUID] }],
    });
    this.#assertConnectAttempt(attempt);
    // Retain browser authorization as soon as the chooser succeeds, even if
    // service discovery or the initial application sync later fails.
    this.#device = device;
    this.#autoReconnectEnabled = true;
    await this.#connect(device, false, attempt);
  }

  async reconnect(preferredDeviceId?: string): Promise<void> {
    const support = webBluetoothSupport();
    if (!support.supported) throw new Error(support.reason);
    const attempt = this.#beginManualConnect();
    const remembered = this.#device?.gatt && (!preferredDeviceId || this.#device.id === preferredDeviceId)
      ? this.#device
      : undefined;
    const devices = remembered || !navigator.bluetooth!.getDevices ? [] : await navigator.bluetooth!.getDevices();
    this.#assertConnectAttempt(attempt);
    const device = remembered
      ?? devices.find((candidate) => candidate.id === preferredDeviceId)
      ?? (devices.length === 1 ? devices[0] : undefined);
    if (!device && !navigator.bluetooth!.getDevices) throw new Error("Choose Pair to reconnect in this browser.");
    if (!device && devices.length > 1) {
      throw new Error("More than one paired controller is available. Choose Pair to select one.");
    }
    if (!device) throw new Error("No previously paired controller is available.");
    this.#device = device;
    this.#autoReconnectEnabled = true;
    await this.#connect(device, false, attempt);
  }

  #connect(device: BluetoothDevice, automatic: boolean, attempt: number): Promise<void> {
    const run = (): Promise<void> => this.#connectNow(device, automatic, attempt);
    const result = this.#connectQueue.then(run, run);
    this.#connectQueue = result.catch(() => undefined);
    return result;
  }

  async #connectNow(device: BluetoothDevice, automatic: boolean, attempt: number): Promise<void> {
    this.#assertConnectAttempt(attempt);
    if (!device.gatt) throw new Error("The selected device does not expose Bluetooth services.");
    const previousServer = this.#server;
    this.#clearGattConnection();
    if (previousServer?.connected) previousServer.disconnect();
    this.#intentionalDisconnect = false;
    this.#retryAfterIntentionalDisconnect = false;
    this.#disconnectReason = undefined;
    this.#listeningDevice = device;
    device.addEventListener("gattserverdisconnected", this.#handleDisconnect);
    let server: BluetoothRemoteGATTServer | undefined;
    let eventCharacteristic: BluetoothRemoteGATTCharacteristic | undefined;
    let liveCharacteristic: BluetoothRemoteGATTCharacteristic | undefined;
    try {
      const connectOperation = device.gatt.connect();
      server = await withDeadline(
        connectOperation,
        BLE_OPERATION_TIMEOUT_MS.connect,
        "Bluetooth connection",
        (lateServer) => {
          if (lateServer !== this.#server && lateServer.connected) lateServer.disconnect();
        },
      );
      this.#assertConnectAttempt(attempt);
      this.#server = server;
      const service = await withDeadline(
        server.getPrimaryService(REMOTE_SERVICE_UUID),
        BLE_OPERATION_TIMEOUT_MS.discovery,
        "Remote service discovery",
      );
      this.#assertConnectAttempt(attempt);

      // Keep setup serialized. Several Android Bluetooth stacks reject
      // concurrent GATT discovery, reads, and CCCD writes.
      const protocolInfo = await withDeadline(
        service.getCharacteristic(PROTOCOL_INFO_UUID),
        BLE_OPERATION_TIMEOUT_MS.discovery,
        "Protocol characteristic discovery",
      );
      const info = viewBytes(await withDeadline(
        protocolInfo.readValue(),
        BLE_OPERATION_TIMEOUT_MS.read,
        "Protocol descriptor read",
      ));
      this.#assertConnectAttempt(attempt);
      if (!isCompatibleProtocolInfo(info)) {
        throw new Error("The controller uses an incompatible remote protocol.");
      }

      const command = await withDeadline(
        service.getCharacteristic(COMMAND_UUID),
        BLE_OPERATION_TIMEOUT_MS.discovery,
        "Command characteristic discovery",
      );
      eventCharacteristic = await withDeadline(
        service.getCharacteristic(EVENT_UUID),
        BLE_OPERATION_TIMEOUT_MS.discovery,
        "Event characteristic discovery",
      );
      liveCharacteristic = await withDeadline(
        service.getCharacteristic(LIVE_STREAM_UUID),
        BLE_OPERATION_TIMEOUT_MS.discovery,
        "Live characteristic discovery",
      );
      this.#assertConnectAttempt(attempt);

      // Install handlers before enabling notifications. Some stacks deliver a
      // cached/queued value before startNotifications() resolves.
      this.#event = eventCharacteristic;
      this.#live = liveCharacteristic;
      eventCharacteristic.addEventListener("characteristicvaluechanged", this.#handleEventValue);
      liveCharacteristic.addEventListener("characteristicvaluechanged", this.#handleLiveValue);
      await withDeadline(
        eventCharacteristic.startNotifications(),
        BLE_OPERATION_TIMEOUT_MS.subscription,
        "Event notification subscription",
      );
      this.#assertConnectAttempt(attempt);
      await withDeadline(
        liveCharacteristic.startNotifications(),
        BLE_OPERATION_TIMEOUT_MS.subscription,
        "Live notification subscription",
      );
      this.#assertConnectAttempt(attempt);

      this.#command = command;
      if (!automatic) this.#autoReconnectAttempt = 0;
      if (automatic) for (const listener of this.#reconnectListeners) listener();
    } catch (error) {
      if (this.#listeningDevice === device) this.#clearGattConnection();
      if (server?.connected) server.disconnect();
      if (attempt === this.#connectAttemptGeneration) this.#intentionalDisconnect = false;
      throw error;
    }
  }

  #handleEventValue = (event: Event): void => {
    this.#receive(event, this.#eventAssembler);
  };

  #handleLiveValue = (event: Event): void => {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    const record = viewBytes(characteristic.value);
    if (record.length !== 20) {
      console.warn("Ignored malformed BLE live notification");
      return;
    }
    for (const listener of this.#liveListeners) listener(record);
  };

  #receive(event: Event, assembler: FrameAssembler): void {
    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    if (!characteristic.value) return;
    try {
      const message = assembler.push(viewBytes(characteristic.value));
      if (message) for (const listener of this.#messageListeners) listener(message);
    } catch (error) {
      console.warn("Ignored malformed BLE notification", error);
    }
  }

  #handleDisconnect = (): void => {
    const reason = this.#intentionalDisconnect
      ? this.#disconnectReason
      : "Controller disconnected. State may have changed; reconnect to resync.";
    const retry = this.#autoReconnectEnabled &&
      (!this.#intentionalDisconnect || this.#retryAfterIntentionalDisconnect);
    this.#invalidateConnectAttempt();
    this.#clearGattConnection();
    this.#intentionalDisconnect = false;
    this.#retryAfterIntentionalDisconnect = false;
    this.#disconnectReason = undefined;
    for (const listener of this.#disconnectListeners) listener(reason);
    if (retry) this.#scheduleAutoReconnect();
  };

  #clearGattConnection(): void {
    this.#listeningDevice?.removeEventListener("gattserverdisconnected", this.#handleDisconnect);
    this.#event?.removeEventListener("characteristicvaluechanged", this.#handleEventValue);
    this.#live?.removeEventListener("characteristicvaluechanged", this.#handleLiveValue);
    this.#eventAssembler.reset();
    this.#connectionGeneration += 1;
    this.#writeQueue = Promise.resolve();
    this.#server = undefined;
    this.#command = undefined;
    this.#event = undefined;
    this.#live = undefined;
    this.#listeningDevice = undefined;
  }

  #scheduleAutoReconnect(): void {
    const device = this.#device;
    const delay = Math.min(
      WebBleTransport.#AUTO_RECONNECT_INITIAL_DELAY_MS * 2 ** Math.min(this.#autoReconnectAttempt, 10),
      WebBleTransport.#AUTO_RECONNECT_MAX_DELAY_MS,
    );
    if (!this.#autoReconnectEnabled || !device?.gatt || this.#autoReconnectTimer !== undefined) return;
    this.#autoReconnectAttempt += 1;
    this.#autoReconnectTimer = window.setTimeout(() => {
      this.#autoReconnectTimer = undefined;
      if (!this.#autoReconnectEnabled || this.connected) return;
      const attempt = ++this.#connectAttemptGeneration;
      void this.#connect(device, true, attempt).catch((error) => {
        if (!(error instanceof SupersededConnectionError) && attempt === this.#connectAttemptGeneration) {
          this.#scheduleAutoReconnect();
        }
      });
    }, delay);
  }

  #cancelAutoReconnect(resetAttempt = true): void {
    if (this.#autoReconnectTimer !== undefined) window.clearTimeout(this.#autoReconnectTimer);
    this.#autoReconnectTimer = undefined;
    if (resetAttempt) this.#autoReconnectAttempt = 0;
  }

  #beginManualConnect(): number {
    this.#autoReconnectEnabled = false;
    this.#cancelAutoReconnect();
    this.#invalidateConnectAttempt();
    return this.#connectAttemptGeneration;
  }

  #assertConnectAttempt(attempt: number): void {
    if (attempt !== this.#connectAttemptGeneration) throw new SupersededConnectionError();
  }

  #invalidateConnectAttempt(): void {
    this.#connectAttemptGeneration += 1;
  }

  disconnect(reason?: string, options?: DisconnectOptions): void {
    const retry = options?.reconnect === true && Boolean(this.#device?.gatt);
    this.#autoReconnectEnabled = retry;
    this.#cancelAutoReconnect(!retry);
    this.#invalidateConnectAttempt();
    this.#intentionalDisconnect = true;
    this.#retryAfterIntentionalDisconnect = retry;
    this.#disconnectReason = reason;
    if (this.#server?.connected) this.#server.disconnect();
    else {
      this.#clearGattConnection();
      this.#intentionalDisconnect = false;
      this.#retryAfterIntentionalDisconnect = false;
      this.#disconnectReason = undefined;
      for (const listener of this.#disconnectListeners) listener(reason);
      if (retry) this.#scheduleAutoReconnect();
    }
  }

  markConnectionReady(): void {
    // Raw GATT reconnection is not enough: application authentication and
    // state resync must succeed before a recovery attempt is considered good.
    this.#autoReconnectAttempt = 0;
  }

  async send(message: ProtocolMessage): Promise<void> {
    if ((message.flags & FrameFlag.Request) === 0) throw new Error("Only request messages may be written");
    const frames = fragmentMessage(message);
    const command = this.#command;
    const generation = this.#connectionGeneration;
    if (!command || !this.connected) throw new Error("Controller is not connected");
    const write = async (): Promise<void> => {
      for (const frame of frames) {
        if (generation !== this.#connectionGeneration || command !== this.#command || !this.connected) {
          throw new Error("Controller connection changed before the request was written.");
        }
        try {
          await withDeadline(
            command.writeValueWithResponse(frame.slice().buffer as ArrayBuffer),
            BLE_OPERATION_TIMEOUT_MS.write,
            "Bluetooth request write",
          );
        } catch (error) {
          if (error instanceof BleOperationTimeoutError && generation === this.#connectionGeneration) {
            this.disconnect(
              "Bluetooth write timed out; reconnecting to verify controller state.",
              { reconnect: true },
            );
          }
          throw error;
        }
        if (generation !== this.#connectionGeneration || command !== this.#command) {
          throw new Error("Controller connection changed while the request was being written.");
        }
      }
    };
    this.#writeQueue = this.#writeQueue.then(write, write);
    await this.#writeQueue;
  }

  onMessage(listener: MessageListener): () => void {
    this.#messageListeners.add(listener);
    return () => this.#messageListeners.delete(listener);
  }

  onLive(listener: LiveListener): () => void {
    this.#liveListeners.add(listener);
    return () => this.#liveListeners.delete(listener);
  }

  onDisconnect(listener: DisconnectListener): () => void {
    this.#disconnectListeners.add(listener);
    return () => this.#disconnectListeners.delete(listener);
  }

  onReconnect(listener: ReconnectListener): () => void {
    this.#reconnectListeners.add(listener);
    return () => this.#reconnectListeners.delete(listener);
  }
}
